import { Button, DialogDisclosure, DialogDismiss, Form } from "@ariakit/react";
import {
  DialogTrigger,
  MiddotSeparated,
  MinWidth,
  SubmitErrorAlert,
  useMediaQuery,
} from "@indietabletop/appkit";
import { fmt } from "../App/fmt.ts";
import { href } from "../App/href.tsx";
import { useCartContext } from "../cart/cart.tsx";
import { type Product } from "../client.ts";
import { fragment } from "../common.css.ts";
import {
  DialogActions,
  DialogContent,
  DialogHeader,
  ScrollableDialog,
} from "../Dialog/Dialog.tsx";
import dividerSrc from "../WarbandPage/divider.svg";
import { CheckoutButton } from "./CheckoutButton.tsx";
import { SHOP_CURRENCY } from "./constants.ts";
import { getKeyFacts, mergeKeyFacts, toType } from "./helpers.ts";
import { KeyFactsList } from "./KeyFacts.tsx";
import { ProductLabel } from "./ProductType.tsx";
import { card, cartDialog, cartPill } from "./ShopPage.css.ts";
import { useCheckoutForm } from "./useCheckoutForm.tsx";

export function ShoppingCartDialog(props: { products: Product[] }) {
  const { products } = props;

  const { form, submitName } = useCheckoutForm({
    products,
    cancelTo: href("~/shop"),
  });
  const cart = useCartContext();
  const facts = mergeKeyFacts(products.map(getKeyFacts));
  const isMediumOrWider = useMediaQuery(MinWidth.MEDIUM);

  return (
    <ScrollableDialog>
      <Form store={form} style={{ display: "contents" }}>
        <DialogHeader heading="Shopping Cart" />

        <DialogContent>
          {products.length > 0 ? (
            <div className={cartDialog.list}>
              {products.map((product) => {
                return (
                  <div
                    key={product.id}
                    className={cartDialog.item({ type: toType(product) })}
                  >
                    {product.thumbnailSrc && (
                      <img
                        src={product.thumbnailSrc}
                        alt=""
                        className={cartDialog.thumbnail}
                      />
                    )}

                    <div className={cartDialog.info}>
                      <div className={cartDialog.name}>{product.name}</div>

                      <MiddotSeparated className={cartDialog.summary}>
                        {!isMediumOrWider && (
                          <span className={cartDialog.summaryPrice}>
                            {fmt.currency(SHOP_CURRENCY, product.price)}
                          </span>
                        )}

                        <span className={card.type}>
                          {ProductLabel.get(product.type)}
                        </span>

                        {product.isEarlyAccess && (
                          <span className={card.ea}>Early Access</span>
                        )}
                      </MiddotSeparated>

                      <div className={cartDialog.actions}>
                        <Button
                          className={cartDialog.remove}
                          onClick={() => void cart.remove(product.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>

                    <div className={cartDialog.price}>
                      {fmt.currency(SHOP_CURRENCY, product.price)}
                    </div>
                  </div>
                );
              })}

              <Button
                onClick={() => void cart.clear()}
                className={cartDialog.clear}
              >
                Clear cart
              </Button>
            </div>
          ) : (
            <div className={cartDialog.empty}>Your cart is empty.</div>
          )}

          <img src={dividerSrc} alt="" className={cartDialog.divider} />

          <KeyFactsList facts={facts} productsCount={products.length} />
        </DialogContent>

        <DialogActions>
          <SubmitErrorAlert name={submitName} className={cartDialog.error} />

          {products.length > 0 ? (
            <CheckoutButton
              className={cartDialog.checkout}
              products={products}
              backToIfAccountNeeded={href("~/shop")}
              redirectToIfAccountNeeded={href("~/shop", {
                search: { note: "continuePurchase" },
              })}
            >
              Checkout {fmt.currency(SHOP_CURRENCY, cart.total)}
            </CheckoutButton>
          ) : (
            <DialogDismiss className={cartDialog.checkout}>Close</DialogDismiss>
          )}
        </DialogActions>
      </Form>
    </ScrollableDialog>
  );
}

export function ShoppingCartPill() {
  const cart = useCartContext();
  const { form, submitName } = useCheckoutForm({
    products: cart.products,
    cancelTo: href("~/shop"),
  });
  const isVisible = !!cart.products.length;

  return (
    <div className={cartPill.container({ isVisible })}>
      <DialogTrigger>
        <ShoppingCartDialog products={cart.products} />
        <DialogDisclosure className={cartPill.trigger}>
          <svg
            className={cartPill.icon}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
          >
            <path d="M15.3699 3.43994L18.5809 9.00194L22.0039 9.00212V11.0021L20.8369 11.0019L20.0803 20.0851C20.0371 20.6034 19.6038 21.0021 19.0837 21.0021H4.92405C4.40396 21.0021 3.97069 20.6034 3.9275 20.0851L3.16991 11.0019L2.00391 11.0021V9.00212L5.42591 9.00194L8.63789 3.43994L10.3699 4.43994L7.73591 9.00194H16.2709L13.6379 4.43994L15.3699 3.43994ZM18.8299 11.0019L5.17691 11.0021L5.84391 19.0021H18.1629L18.8299 11.0019ZM13.0039 13.0021V17.0021H11.0039V13.0021H13.0039ZM9.00391 13.0021V17.0021H7.00391V13.0021H9.00391ZM17.0039 13.0021V17.0021H15.0039V13.0021H17.0039Z" />
          </svg>

          <div>
            <div className={cartPill.label}>Shopping Cart</div>
            <div className={cartPill.summary}>
              {fmt.plural(cart.products.length, {
                one: "# item",
                other: "# items",
              })}
              {", "}
              {fmt.currency(SHOP_CURRENCY, cart.total)}
            </div>
          </div>
        </DialogDisclosure>
      </DialogTrigger>

      <Form store={form} className={fragment}>
        <CheckoutButton
          className={cartPill.checkout}
          products={cart.products}
          backToIfAccountNeeded={href("~/shop")}
          redirectToIfAccountNeeded={href("~/shop", {
            search: { note: "continuePurchase" },
          })}
        >
          Checkout
        </CheckoutButton>

        <SubmitErrorAlert name={submitName} />
      </Form>
    </div>
  );
}
